import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  SiteSettings,
  Article,
  Category,
  Product,
  PortfolioItem,
  Comment,
  AdBanner,
  GalleryItem,
  PageRoute,
  AppUser,
} from '../types.ts';

interface AppContextType {
  settings: SiteSettings | null;
  currentPage: PageRoute;
  currentArticleSlug: string | null;
  currentProductSlug: string | null;
  currentPortfolioSlug: string | null;
  currentCustomPageSlug: string | null;
  selectedCategory: string;
  searchQuery: string;
  articles: Article[];
  categories: Category[];
  products: Product[];
  portfolio: PortfolioItem[];
  ads: AdBanner[];
  gallery: GalleryItem[];
  users: AppUser[];
  isLoading: boolean;
  user: AppUser | null;
  token: string | null;
  toast: { id?: string; title?: string; message: string; type: 'success' | 'error' | 'info' | 'warning'; duration?: number } | null;
  selectedProduct: Product | null;
  selectedPortfolio: PortfolioItem | null;
  customPathSlug: string | null; // For secret URL check
  authorModalData: { name: string; avatar?: string } | null;

  navigate: (page: PageRoute, slug?: string, queryParams?: Record<string, string>) => void;
  copyPageUrl: (urlOrPath?: string, customLabel?: string) => Promise<boolean>;
  setSelectedCategory: (cat: string) => void;
  setSearchQuery: (query: string) => void;
  setSelectedProduct: (prod: Product | null) => void;
  setSelectedPortfolio: (item: PortfolioItem | null) => void;
  openAuthorModal: (name: string, avatar?: string) => void;
  closeAuthorModal: () => void;
  showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning', title?: string, duration?: number) => void;
  hideToast: () => void;
  loginUser: (userData: any, token: string) => void;
  logoutUser: () => void;
  refreshData: () => Promise<void>;
  fetchUsers: () => Promise<void>;
  addUser: (userData: any) => Promise<boolean>;
  editUser: (id: string, userData: any) => Promise<boolean>;
  removeUser: (id: string) => Promise<boolean>;
  updateThemeLocally: (themeUpdates: Partial<SiteSettings['theme']>) => void;
  updateSettingsOnServer: (newSettings: Partial<SiteSettings>) => Promise<boolean>;
}

// Robust helper to safely write to localStorage without throwing QuotaExceededError
export function safeSetLocalStorage(key: string, value: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err: any) {
    console.warn(`[Storage] Failed to cache ${key} in localStorage (${err?.name || 'QuotaError'}):`, err?.message || err);
    // If quota exceeded, proactively clear large non-essential caches to protect app stability
    if (
      err?.name === 'QuotaExceededError' ||
      err?.code === 22 ||
      err?.code === 1014 ||
      (err?.message && err.message.toLowerCase().includes('quota'))
    ) {
      try {
        localStorage.removeItem('nusantara_articles');
        localStorage.removeItem('nusantara_portfolio');
        localStorage.removeItem('nusantara_products');
        localStorage.removeItem('nusantara_gallery');
        localStorage.removeItem('nusantara_ads');
        if (key !== 'nusantara_articles') {
          localStorage.setItem(key, value);
          return true;
        }
      } catch {
        // Silently ignore secondary quota error
      }
    }
    return false;
  }
}

// Auto cleanup bloated legacy localStorage cache on startup
if (typeof window !== 'undefined') {
  try {
    const rawArticles = localStorage.getItem('nusantara_articles');
    if (rawArticles && rawArticles.length > 200000) { // If larger than 200KB
      localStorage.removeItem('nusantara_articles');
      console.info('[Storage] Cleared oversized articles cache to prevent QuotaExceededError');
    }
  } catch {
    // ignore
  }
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings | null>(() => {
    if (typeof window !== 'undefined' && (window as any).__INITIAL_SETTINGS__) {
      return (window as any).__INITIAL_SETTINGS__;
    }
    try {
      const saved = localStorage.getItem('nusantara_site_settings');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [currentPage, setCurrentPage] = useState<PageRoute>('home');
  const [currentArticleSlug, setCurrentArticleSlug] = useState<string | null>(null);
  const [currentProductSlug, setCurrentProductSlug] = useState<string | null>(null);
  const [currentPortfolioSlug, setCurrentPortfolioSlug] = useState<string | null>(null);
  const [currentCustomPageSlug, setCurrentCustomPageSlug] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [articles, setArticles] = useState<Article[]>(() => {
    try {
      const saved = localStorage.getItem('nusantara_articles');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [categories, setCategories] = useState<Category[]>(() => {
    try {
      const saved = localStorage.getItem('nusantara_categories');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [products, setProducts] = useState<Product[]>(() => {
    try {
      const saved = localStorage.getItem('nusantara_products');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>(() => {
    try {
      const saved = localStorage.getItem('nusantara_portfolio');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [ads, setAds] = useState<AdBanner[]>(() => {
    try {
      const saved = localStorage.getItem('nusantara_ads');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [gallery, setGallery] = useState<GalleryItem[]>(() => {
    try {
      const saved = localStorage.getItem('nusantara_gallery');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [users, setUsers] = useState<AppUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && (window as any).__INITIAL_SETTINGS__) {
      return false;
    }
    try {
      const saved = localStorage.getItem('nusantara_site_settings');
      return !saved;
    } catch {
      return false;
    }
  });
  const [toast, setToast] = useState<{ id?: string; title?: string; message: string; type: 'success' | 'error' | 'info' | 'warning'; duration?: number } | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioItem | null>(null);
  const [customPathSlug, setCustomPathSlug] = useState<string | null>(null);
  const [authorModalData, setAuthorModalData] = useState<{ name: string; avatar?: string } | null>(null);

  const openAuthorModal = (name: string, avatar?: string) => {
    if (!name) return;
    setAuthorModalData({ name: name.trim(), avatar });
  };

  const closeAuthorModal = () => {
    setAuthorModalData(null);
  };

  // Auth
  const [user, setUser] = useState<AppUser | null>(() => {
    const saved = localStorage.getItem('nusantara_admin_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('nusantara_admin_token');
  });

  const showToast = (
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'success',
    title?: string,
    duration: number = 4500
  ) => {
    const id = Date.now().toString();
    setToast({ id, message, type, title, duration });
    setTimeout(() => {
      setToast((prev) => (prev?.id === id ? null : prev));
    }, duration);
  };

  const hideToast = () => {
    setToast(null);
  };

  const copyPageUrl = async (urlOrPath?: string, customLabel?: string): Promise<boolean> => {
    try {
      let target = '';
      if (!urlOrPath) {
        target = window.location.href;
      } else if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
        target = urlOrPath;
      } else {
        const origin = window.location.origin;
        const cleanPath = urlOrPath.startsWith('/') ? urlOrPath : `/${urlOrPath}`;
        target = `${origin}${cleanPath}`;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(target);
      } else {
        // Fallback for older browsers or restricted iframes
        const textArea = document.createElement('textarea');
        textArea.value = target;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      showToast(
        `Tautan URL ${customLabel || 'halaman'} berhasil disalin ke clipboard! Siap dibagikan ke WhatsApp / media sosial.`,
        'success',
        'URL Disalin'
      );
      return true;
    } catch (err) {
      console.error('Failed to copy URL:', err);
      showToast('Gagal menyalin tautan secara otomatis. Silakan salin manual dari address bar.', 'error');
      return false;
    }
  };

  const loginUser = (userData: any, userToken: string) => {
    setUser(userData);
    setToken(userToken);
    safeSetLocalStorage('nusantara_admin_user', JSON.stringify(userData));
    safeSetLocalStorage('nusantara_admin_token', userToken);
    setCurrentPage('admin');
    showToast(`Selamat datang kembali, ${userData.name}!`, 'success');
  };

  const logoutUser = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('nusantara_admin_user');
    localStorage.removeItem('nusantara_admin_token');
    setCurrentPage('home');
    showToast('Anda telah berhasil keluar (logout)', 'info');
  };

  const parseRouteFromPath = (pathString: string, currentSettings?: SiteSettings | null) => {
    const activeSettings = currentSettings !== undefined ? currentSettings : settings;
    const path = pathString.replace(/^\/+|\/+$/g, '');

    // Check if URL search params have tag or search filter
    if (typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const tagParam = urlParams.get('tag') || urlParams.get('tagar');
        if (tagParam) {
          setSearchQuery(decodeURIComponent(tagParam));
          setCurrentPage('blog');
          if (!path || path === 'blog' || path === 'artikel') return;
        }
      } catch {
        // ignore url parsing error in restricted environments
      }
    }

    if (!path) {
      setCurrentPage('home');
      return;
    }

    // Direct /tag/tag-name or /tagar/tag-name deep link support
    if (path.startsWith('tag/') || path.startsWith('tagar/')) {
      const tagFromPath = decodeURIComponent(path.replace(/^(tag|tagar)\//, '').trim());
      if (tagFromPath) {
        setSearchQuery(tagFromPath);
        setCurrentPage('blog');
        return;
      }
    }

    const blogSlug = (activeSettings?.pageSlugs?.articles || activeSettings?.pageSlugs?.blogSlug || 'blog').replace(/^\/+|\/+$/g, '').toLowerCase();
    const productsSlug = (activeSettings?.pageSlugs?.products || activeSettings?.pageSlugs?.productsSlug || 'produk').replace(/^\/+|\/+$/g, '').toLowerCase();
    const portfolioSlug = (activeSettings?.pageSlugs?.portfolio || activeSettings?.pageSlugs?.portfolioSlug || 'portofolio').replace(/^\/+|\/+$/g, '').toLowerCase();
    const gallerySlug = (activeSettings?.pageSlugs?.gallery || activeSettings?.pageSlugs?.gallerySlug || 'foto-video').replace(/^\/+|\/+$/g, '').toLowerCase();

    // Check article detail or blog page
    const blogPrefixes = Array.from(new Set([blogSlug, 'article', 'artikel', 'berita', 'blog', 'articles'])).filter(Boolean);
    const blogPrefixRegex = new RegExp(`^(${blogPrefixes.join('|')})(/|$)`, 'i');

    if (blogPrefixRegex.test(path)) {
      const parts = path.split('/');
      if (parts.length > 1 && parts[1].trim()) {
        setCurrentPage('article-detail');
        setCurrentArticleSlug(parts.slice(1).join('/'));
      } else {
        setCurrentPage('blog');
      }
      return;
    }

    // Check product detail or products page
    const productPrefixes = Array.from(new Set([productsSlug, 'product', 'produk', 'layanan', 'products', 'marketplace', 'services'])).filter(Boolean);
    const productPrefixRegex = new RegExp(`^(${productPrefixes.join('|')})(/|$)`, 'i');

    if (productPrefixRegex.test(path)) {
      const parts = path.split('/');
      if (parts.length > 1 && parts[1].trim()) {
        setCurrentPage('product-detail');
        setCurrentProductSlug(parts.slice(1).join('/'));
      } else {
        setCurrentPage('products');
      }
      return;
    }

    // Check portfolio
    const portfolioPrefixes = Array.from(new Set([portfolioSlug, 'portfolio', 'portofolio', 'proyek', 'karya'])).filter(Boolean);
    const portfolioPrefixRegex = new RegExp(`^(${portfolioPrefixes.join('|')})(/|$)`, 'i');

    if (portfolioPrefixRegex.test(path)) {
      const parts = path.split('/');
      if (parts.length > 1 && parts[1].trim()) {
        setCurrentPage('portfolio-detail');
        setCurrentPortfolioSlug(parts.slice(1).join('/'));
      } else {
        setCurrentPage('portfolio');
      }
      return;
    }

    // Check gallery
    const galleryPrefixes = Array.from(new Set([gallerySlug, 'gallery', 'galeri', 'foto-video', 'dokumentasi', 'video'])).filter(Boolean);
    const galleryPrefixRegex = new RegExp(`^(${galleryPrefixes.join('|')})(/|$)`, 'i');

    if (galleryPrefixRegex.test(path)) {
      setCurrentPage('gallery');
      return;
    }

    // Check media showcase
    const mediaShowcaseSlug = (activeSettings?.pageSlugs?.mediaShowcase || activeSettings?.pageSlugs?.mediaShowcaseSlug || 'media-showcase').replace(/^\/+|\/+$/g, '').toLowerCase();
    const mediaPrefixes = Array.from(new Set([mediaShowcaseSlug, 'media-showcase', 'sorotan-media', 'foto-showcase', 'media-highlights'])).filter(Boolean);
    const mediaPrefixRegex = new RegExp(`^(${mediaPrefixes.join('|')})(/|$)`, 'i');

    if (mediaPrefixRegex.test(path)) {
      setCurrentPage('media-showcase');
      return;
    }

    const secretPath = (activeSettings?.security?.customLoginPath || '').replace(/^\/+|\/+$/g, '').toLowerCase().trim();
    const cleanPath = path.toLowerCase().trim();

    if (path.startsWith('page/') || path.startsWith('halaman/')) {
      const slug = path.replace(/^(page|halaman)\//, '').trim();
      if (slug) {
        setCurrentPage('custom-page');
        setCurrentCustomPageSlug(slug);
      } else {
        setCurrentPage('home');
      }
    } else if (cleanPath === 'about' || cleanPath === 'tentang' || cleanPath === 'tentang-kami') {
      setCurrentPage('about');
    } else if (cleanPath === 'contact' || cleanPath === 'kontak' || cleanPath === 'hubungi-kami') {
      setCurrentPage('contact');
    } else if (secretPath && cleanPath === secretPath) {
      // Direct access to custom secret login URL (e.g. /yuklogin)
      if (user) {
        setCurrentPage('admin');
      } else {
        setCurrentPage('login');
      }
    } else if (cleanPath === 'admin') {
      if (user) {
        setCurrentPage('admin');
      } else {
        // If a secret login path is active, hide the login page from /admin!
        if (secretPath) {
          setCurrentPage('home');
          try {
            window.history.replaceState({}, '', '/');
          } catch {}
        } else {
          setCurrentPage('login');
        }
      }
    } else if (cleanPath === 'login') {
      if (user) {
        setCurrentPage('admin');
      } else {
        // If a secret login path is active, hide the login page from /login!
        if (secretPath) {
          setCurrentPage('home');
          try {
            window.history.replaceState({}, '', '/');
          } catch {}
        } else {
          setCurrentPage('login');
        }
      }
    } else {
      // Potential custom page slug or secret login URL
      if (secretPath && cleanPath === secretPath) {
        if (user) {
          setCurrentPage('admin');
        } else {
          setCurrentPage('login');
        }
      } else {
        setCustomPathSlug(path);
      }
    }
  };

  // Check URL pathname for direct deep links and secret login paths on initial load
  useEffect(() => {
    // Apply cached theme variables immediately on mount
    if (settings) {
      applyThemeVariables(settings.theme, settings.articleTypography);
    }

    parseRouteFromPath(window.location.pathname);

    // Add popstate listener for browser forward/backward buttons
    const handlePopState = () => {
      parseRouteFromPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (page: PageRoute, slug?: string, queryParams?: Record<string, string>) => {
    setCurrentPage(page);
    if (page === 'article-detail' && slug) {
      setCurrentArticleSlug(slug);
    } else if (page === 'product-detail' && slug) {
      setCurrentProductSlug(slug);
    } else if (page === 'portfolio-detail' && slug) {
      setCurrentPortfolioSlug(slug);
    } else if (page === 'custom-page' && slug) {
      setCurrentCustomPageSlug(slug);
    }

    // Update browser URL history with clean readable slugs
    try {
      const blogSlug = (settings?.pageSlugs?.articles || settings?.pageSlugs?.blogSlug || 'blog').replace(/^\/+|\/+$/g, '') || 'blog';
      const productsSlug = (settings?.pageSlugs?.products || settings?.pageSlugs?.productsSlug || 'produk').replace(/^\/+|\/+$/g, '') || 'produk';
      const portfolioSlug = (settings?.pageSlugs?.portfolio || settings?.pageSlugs?.portfolioSlug || 'portofolio').replace(/^\/+|\/+$/g, '') || 'portofolio';
      const gallerySlug = (settings?.pageSlugs?.gallery || settings?.pageSlugs?.gallerySlug || 'foto-video').replace(/^\/+|\/+$/g, '') || 'foto-video';
      const mediaShowcaseSlug = (settings?.pageSlugs?.mediaShowcase || settings?.pageSlugs?.mediaShowcaseSlug || 'media-showcase').replace(/^\/+|\/+$/g, '') || 'media-showcase';

      let targetPath = '/';
      if (page === 'article-detail' && slug) targetPath = `/${blogSlug}/${slug}`;
      else if (page === 'product-detail' && slug) targetPath = `/${productsSlug}/${slug}`;
      else if (page === 'portfolio-detail' && slug) targetPath = `/${portfolioSlug}/${slug}`;
      else if (page === 'custom-page' && slug) targetPath = `/page/${slug}`;
      else if (page === 'about') targetPath = '/about';
      else if (page === 'contact') targetPath = '/contact';
      else if (page === 'products') targetPath = `/${productsSlug}`;
      else if (page === 'portfolio') targetPath = `/${portfolioSlug}`;
      else if (page === 'blog') {
        if (queryParams?.tag) {
          targetPath = `/tag/${encodeURIComponent(queryParams.tag)}`;
        } else if (queryParams && Object.keys(queryParams).length > 0) {
          const qs = new URLSearchParams(queryParams).toString();
          targetPath = `/${blogSlug}?${qs}`;
        } else {
          targetPath = `/${blogSlug}`;
        }
      }
      else if (page === 'gallery') targetPath = `/${gallerySlug}`;
      else if (page === 'media-showcase') targetPath = `/${mediaShowcaseSlug}`;
      else if (page === 'admin') targetPath = '/admin';
      else if (page === 'login') targetPath = '/login';

      window.history.pushState({}, '', targetPath);
    } catch {
      // In sandbox iframes history pushState may be restricted
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const safeFetchJson = async (url: string) => {
    try {
      const res = await fetch(url);
      if (!res.ok) return { success: false };
      const text = await res.text();
      if (!text || !text.trim()) return { success: false };
      return JSON.parse(text);
    } catch {
      return { success: false };
    }
  };

  const cleanDataImages = <T,>(val: T): T => {
    if (typeof val === 'string') {
      if (val.startsWith('/data:image/') || val.startsWith('/data:video/')) {
        return val.substring(1) as unknown as T;
      }
      return val;
    }
    if (Array.isArray(val)) {
      return val.map(cleanDataImages) as unknown as T;
    }
    if (val !== null && typeof val === 'object') {
      const res: any = {};
      for (const k of Object.keys(val)) {
        res[k] = cleanDataImages((val as any)[k]);
      }
      return res;
    }
    return val;
  };

  const fetchArticles = async () => {
    try {
      const res = await safeFetchJson('/api/articles');
      if (res.success) {
        setArticles(cleanDataImages(res.data || []));
      }
    } catch {}
  };

  const fetchProducts = async () => {
    try {
      const res = await safeFetchJson('/api/products');
      if (res.success) {
        setProducts(cleanDataImages(res.data || []));
      }
    } catch {}
  };

  const fetchPortfolio = async () => {
    try {
      const res = await safeFetchJson('/api/portfolio');
      if (res.success) {
        setPortfolio(cleanDataImages(res.data || []));
      }
    } catch {}
  };

  const fetchGallery = async () => {
    try {
      const res = await safeFetchJson('/api/gallery');
      if (res.success) {
        setGallery(cleanDataImages(res.data || []));
      }
    } catch {}
  };

  const fetchAds = async () => {
    try {
      const res = await safeFetchJson('/api/ads');
      if (res.success) {
        setAds(cleanDataImages(res.data || []));
      }
    } catch {}
  };

  const refreshData = async (isInitial: boolean = false) => {
    try {
      if (isInitial) {
        const hasCache = (typeof window !== 'undefined' && (window as any).__INITIAL_SETTINGS__) || localStorage.getItem('nusantara_site_settings');
        if (!hasCache) {
          setIsLoading(true);
        }
      }

      // Minimal initial load for public visitors: ONLY settings and categories for header/nav
      const [setRes, catRes] = await Promise.all([
        safeFetchJson('/api/settings'),
        safeFetchJson('/api/categories'),
      ]);

      if (setRes.success && setRes.data) {
        const cleanedSettings = cleanDataImages(setRes.data);
        setSettings(cleanedSettings);
        if (typeof window !== 'undefined') {
          (window as any).__INITIAL_SETTINGS__ = cleanedSettings;
        }
        safeSetLocalStorage('nusantara_site_settings', JSON.stringify(cleanedSettings));
        applyThemeVariables(cleanedSettings.theme, cleanedSettings.articleTypography);

        const sName = (cleanedSettings.siteName || '').trim();
        const tLine = (cleanedSettings.tagline || '').trim();
        const dynamicTitle = (cleanedSettings.seo?.metaTitle?.trim()) || (tLine && sName ? `${sName} - ${tLine}` : sName);
        if (dynamicTitle && (window.location.pathname === '/' || window.location.pathname === '/index.html')) {
          document.title = dynamicTitle;
          const titleEl = document.getElementById('site-head-title');
          if (titleEl) titleEl.textContent = dynamicTitle;
        }

        if (isInitial) {
          parseRouteFromPath(window.location.pathname, cleanedSettings);
        }

        // Check if customPathSlug matched secret login URL on initial unauthenticated visit only
        const currentPathname = window.location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase().trim();
        const secret = (cleanedSettings.security?.customLoginPath || '').replace(/^\/+|\/+$/g, '').toLowerCase().trim();
        if (secret && !user) {
          if (customPathSlug?.toLowerCase() === secret || currentPathname === secret) {
            setCurrentPage('login');
          } else if (currentPathname === 'admin' || currentPathname === 'login') {
            setCurrentPage('home');
            try {
              window.history.replaceState({}, '', '/');
            } catch {}
          }
        }
      }

      if (catRes && catRes.success) {
        setCategories(catRes.data || []);
        safeSetLocalStorage('nusantara_categories', JSON.stringify(catRes.data || []));
      }
    } catch (err) {
      console.error('Failed to load initial data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const applyThemeVariables = (theme: SiteSettings['theme'], typography?: SiteSettings['articleTypography']) => {
    if (!theme) return;
    const root = document.documentElement;
    if (theme.primaryColor) root.style.setProperty('--primary-color', theme.primaryColor);
    if (theme.secondaryColor) root.style.setProperty('--secondary-color', theme.secondaryColor);
    if (theme.homepageBgColor) root.style.setProperty('--homepage-bg', theme.homepageBgColor);
    if (theme.pageBgColor) root.style.setProperty('--page-bg', theme.pageBgColor);
    if (theme.cardBgColor) root.style.setProperty('--card-bg', theme.cardBgColor);
    if (theme.textColor) root.style.setProperty('--text-main', theme.textColor);
    if (theme.articlePageBgColor) root.style.setProperty('--article-page-bg', theme.articlePageBgColor);
    if (theme.articleTitleColor) root.style.setProperty('--article-title', theme.articleTitleColor);
    if (theme.categoryPageBgColor) root.style.setProperty('--category-page-bg', theme.categoryPageBgColor);
    if (theme.categoryTitleColor) root.style.setProperty('--category-title', theme.categoryTitleColor);
    if (theme.headingFont) {
      if (theme.headingFont === 'Oleo Script') {
        root.style.setProperty('--font-heading', `'Oleo Script', cursive, sans-serif`);
      } else if (theme.headingFont === 'Playfair Display') {
        root.style.setProperty('--font-heading', `'Playfair Display', serif, sans-serif`);
      } else {
        root.style.setProperty('--font-heading', `'${theme.headingFont}', sans-serif`);
      }
    }
    if (theme.bodyFont) root.style.setProperty('--font-body', `'${theme.bodyFont}', sans-serif`);

    // Article Typography
    if (typography) {
      if (typography.fontSizeMobile) root.style.setProperty('--article-font-mobile', typography.fontSizeMobile);
      if (typography.fontSizeTablet) root.style.setProperty('--article-font-tablet', typography.fontSizeTablet);
      if (typography.fontSizeDesktop) root.style.setProperty('--article-font-desktop', typography.fontSizeDesktop);
      if (typography.lineHeight) {
        const lh = typography.lineHeight === 'tight' ? '1.5' : typography.lineHeight === 'relaxed' ? '1.9' : '1.75';
        root.style.setProperty('--article-line-height', lh);
      }
    }
  };

  const updateThemeLocally = (themeUpdates: Partial<SiteSettings['theme']>) => {
    if (!settings) return;
    const newTheme = { ...settings.theme, ...themeUpdates };
    const updated = { ...settings, theme: newTheme };
    setSettings(updated);
    localStorage.setItem('nusantara_site_settings', JSON.stringify(updated));
    applyThemeVariables(newTheme, settings.articleTypography);
  };

  const fetchUsers = async () => {
    const currentToken = token || localStorage.getItem('nusantara_admin_token');
    if (!currentToken) return;
    try {
      const res = await fetch('/api/users', {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      const json = await res.json();
      if (json.success && json.data) {
        setUsers(json.data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const addUser = async (userData: any): Promise<boolean> => {
    try {
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      const json = await res.json();
      if (json.success && json.data) {
        setUsers((prev) => [...prev, json.data]);
        showToast(json.message || 'Pengguna berhasil ditambahkan!', 'success');
        return true;
      }
      showToast(json.message || 'Gagal menambahkan pengguna', 'error');
      return false;
    } catch (err: any) {
      console.error(err);
      showToast('Terjadi kesalahan jaringan saat menambah pengguna', 'error');
      return false;
    }
  };

  const editUser = async (id: string, userData: any): Promise<boolean> => {
    try {
      const res = await fetch(`/api/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      const json = await res.json();
      if (json.success && json.data) {
        setUsers((prev) => prev.map((u) => (u.id === id ? json.data : u)));
        // If current user is editing themselves
        if (user && user.id === id) {
          const updatedSelf = { ...user, ...json.data };
          setUser(updatedSelf);
          localStorage.setItem('nusantara_admin_user', JSON.stringify(updatedSelf));
        }
        showToast(json.message || 'Pengguna berhasil diperbarui!', 'success');
        return true;
      }
      showToast(json.message || 'Gagal memperbarui pengguna', 'error');
      return false;
    } catch (err: any) {
      console.error(err);
      showToast('Terjadi kesalahan jaringan saat memperbarui pengguna', 'error');
      return false;
    }
  };

  const removeUser = async (id: string): Promise<boolean> => {
    try {
      const res = await fetch(`/api/users/${id}`, {
        method: 'DELETE',
      });
      const json = await res.json();
      if (json.success) {
        setUsers((prev) => prev.filter((u) => u.id !== id));
        showToast(json.message || 'Pengguna berhasil dihapus', 'success');
        return true;
      }
      showToast(json.message || 'Gagal menghapus pengguna', 'error');
      return false;
    } catch (err: any) {
      console.error(err);
      showToast('Terjadi kesalahan jaringan saat menghapus pengguna', 'error');
      return false;
    }
  };

  const updateSettingsOnServer = async (newSettings: Partial<SiteSettings>): Promise<boolean> => {
    try {
      const res = await fetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings),
      });
      const json = await res.json();
      if (json.success && json.data) {
        setSettings(json.data);
        if (typeof window !== 'undefined') {
          (window as any).__INITIAL_SETTINGS__ = json.data;
        }
        localStorage.setItem('nusantara_site_settings', JSON.stringify(json.data));
        applyThemeVariables(json.data.theme, json.data.articleTypography);

        // Instantly update browser tab title and favicon without needing a refresh
        const sName = (json.data.siteName || '').trim();
        const tLine = (json.data.tagline || '').trim();
        const dynamicTitle = (json.data.seo?.metaTitle?.trim()) || (tLine && sName ? `${sName} - ${tLine}` : (sName || ''));
        if (dynamicTitle) {
          document.title = dynamicTitle;
          const titleEl = document.getElementById('site-head-title');
          if (titleEl) titleEl.textContent = dynamicTitle;
        }
        const customFavicon = json.data.theme?.faviconUrl || json.data.faviconUrl || json.data.logoUrl;
        if (customFavicon) {
          const favLink = document.getElementById('favicon-link') as HTMLLinkElement | null;
          if (favLink) favLink.href = customFavicon;
          const favShort = document.getElementById('shortcut-favicon-link') as HTMLLinkElement | null;
          if (favShort) favShort.href = customFavicon;
        }

        showToast(
          'Konfigurasi website berhasil disimpan dan telah diterapkan ke sistem.',
          'success',
          'Pengaturan Berhasil Disimpan!'
        );
        return true;
      }
      showToast(
        json.message || 'Gagal menyimpan perubahan pengaturan ke server.',
        'error',
        'Gagal Menyimpan'
      );
      return false;
    } catch (err) {
      console.error(err);
      showToast(
        'Terjadi kesalahan koneksi jaringan saat mencoba menyimpan pengaturan.',
        'error',
        'Koneksi Gagal'
      );
      return false;
    }
  };

  useEffect(() => {
    refreshData(true);
  }, []);

  // Lazy route-based data fetcher: Only request collections when navigating to pages that require them
  useEffect(() => {
    if (currentPage === 'home') {
      fetchArticles();
      fetchProducts();
      fetchPortfolio();
      fetchGallery();
      fetchAds();
    } else if (currentPage === 'blog') {
      fetchArticles();
    } else if (currentPage === 'products' || currentPage === 'product-detail') {
      fetchProducts();
    } else if (currentPage === 'portfolio' || currentPage === 'portfolio-detail') {
      fetchPortfolio();
    } else if (currentPage === 'gallery' || currentPage === 'media-showcase') {
      fetchGallery();
    } else if (currentPage === 'admin') {
      if (user || localStorage.getItem('nusantara_admin_user')) {
        fetchUsers();
        fetchArticles();
        fetchProducts();
        fetchPortfolio();
        fetchGallery();
        fetchAds();
      }
    }
  }, [currentPage, user, token]);

  return (
    <AppContext.Provider
      value={{
        settings,
        currentPage,
        currentArticleSlug,
        currentProductSlug,
        currentPortfolioSlug,
        currentCustomPageSlug,
        selectedCategory,
        searchQuery,
        articles,
        categories,
        products,
        portfolio,
        ads,
        gallery,
        users,
        isLoading,
        user,
        token,
        toast,
        selectedProduct,
        selectedPortfolio,
        customPathSlug,
        authorModalData,
        navigate,
        copyPageUrl,
        setSelectedCategory,
        setSearchQuery,
        setSelectedProduct,
        setSelectedPortfolio,
        openAuthorModal,
        closeAuthorModal,
        showToast,
        hideToast,
        loginUser,
        logoutUser,
        refreshData,
        fetchUsers,
        addUser,
        editUser,
        removeUser,
        updateThemeLocally,
        updateSettingsOnServer,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
